import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  CommandList,
  CommandEmpty,
  Command,
  CommandInput,
  CommandItem,
  CommandGroup,
} from "web-design-system";

import { ChevronsUpDown, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { useInfiniteSchemeMasterList } from "creso-core/hooks";
import { SchemeMaster } from "creso-core/types";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { InView } from "react-intersection-observer";

interface SchemeMasterSearchDropdownProps {
  value: { label: string; value: string } | null;
  onChange: (value: SchemeMaster | null) => void;
  placeholder?: string;
  align?: "start" | "center" | "end";
  disabled?: boolean;
}

export function SchemeMasterSearchDropdown({
  value,
  onChange,
  placeholder,
  align = "center",
  disabled = false,
}: SchemeMasterSearchDropdownProps) {
  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState("");

  const [debouncedSearch] = useDebounceValue(search, 500);

  const { schemes, fetchNextPage, hasNextPage, isPlaceholderData } =
    useInfiniteSchemeMasterList(
      { search: debouncedSearch, pageSize: 20 },
      { enabled: open }
    );

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-disabled={disabled}
          className={cn(
            "w-full justify-between shadow-none",
            disabled && "cursor-not-allowed bg-accent pointer-events-none"
          )}
          onClick={(e) => disabled && e.preventDefault()}
        >
          <div
            className={cn(
              "truncate font-normal text-left",
              !value && "text-muted-foreground"
            )}
          >
            {value?.label || placeholder}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="p-0 w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width)"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={(v) => setSearch(v)}
            placeholder="Search scheme..."
          />
          <CommandList className={cn(isPlaceholderData && "opacity-50")}>
            <CommandEmpty>No scheme found</CommandEmpty>
            <CommandGroup>
              {schemes?.map((scheme) => {
                const optionValue = scheme.fund_scheme_id.toString();
                return (
                  <CommandItem
                    key={optionValue}
                    value={optionValue}
                    onSelect={() => {
                      onChange(value?.value === optionValue ? null : scheme);
                      setOpen(false);
                    }}
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <AmcLogo amc_code={scheme.amc_morningstar_code} />
                      {scheme.scheme_name}
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value?.value === optionValue
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </div>
                  </CommandItem>
                );
              })}
              {hasNextPage && (
                <InView
                  as="div"
                  onChange={(inView) => {
                    if (inView) {
                      fetchNextPage();
                    }
                  }}
                  className="flex justify-center items-center w-full py-2"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                </InView>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
